import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const quickPrompts = [
  'I need Python training',
  'Show placement support',
  'I want online classes',
  'Show contact details',
];

const courseReplies = [
  {
    keywords: ['python', 'ai', 'ml', 'machine learning'],
    text: 'Vedanskrix offers Python, Python with AI, and Machine Learning focused training with practical projects and job-oriented support.',
    links: [
      { to: '/programming', label: 'View Programming Courses' },
      { to: '/contact', label: 'Enquire Now' },
    ],
  },
  {
    keywords: ['android', 'iphone', 'ios', 'mobile'],
    text: 'For mobile development, you can explore Android, iPhone, and app-focused courses with practical guidance.',
    links: [
      { to: '/programming', label: 'See Mobile Courses' },
      { to: '/contact', label: 'Talk To Counsellor' },
    ],
  },
  {
    keywords: ['job', 'placement', 'career', 'interview'],
    text: 'Vedanskrix supports students with resume help, mock interviews, and job placement assistance after training.',
    links: [
      { to: '/contact', label: 'Ask About Placement' },
      { to: '/blog', label: 'Read Training Blog' },
    ],
  },
  {
    keywords: ['online', 'offline', 'weekend', 'batch'],
    text: 'We provide offline, online, and weekend batch options depending on the course and your schedule.',
    links: [
      { to: '/contact', label: 'Check Batch Availability' },
    ],
  },
];

function buildReply(message) {
  const query = message.toLowerCase();

  if (/(hi|hello|hey|namaste)/.test(query)) {
    return {
      text: 'Hello! I can help with courses, placements, batch modes, and contact details for Vedanskrix IT Training.',
      links: [{ to: '/contact', label: 'Contact IT Training' }],
    };
  }

  if (/(contact|call|phone|email|address)/.test(query)) {
    return {
      text: 'You can contact Vedanskrix IT Training at +91 87586 37022 or vedanskrix@gmail.com.',
      links: [
        { to: '/contact', label: 'Open Contact Page' },
        { href: 'tel:+918758637022', label: 'Call Now' },
      ],
    };
  }

  const match = courseReplies.find((item) => item.keywords.some((keyword) => query.includes(keyword)));
  if (match) {
    return {
      text: match.text,
      links: match.links,
    };
  }

  return {
    text: 'I can guide you about IT courses, placement support, class modes, and admissions. Choose a quick prompt or open the contact page.',
    links: [
      { to: '/programming', label: 'Browse Courses' },
      { to: '/contact', label: 'Send Enquiry' },
    ],
  };
}

function AiChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 'welcome-message',
      sender: 'assistant',
      text: 'Welcome to Vedanskrix IT Training. Ask about courses, placements, or batches.',
    },
  ]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const sendMessage = (rawMessage) => {
    const text = rawMessage.trim();
    if (!text) {
      return;
    }

    const reply = buildReply(text);

    setMessages((current) => [
      ...current,
      { id: `user-${Date.now()}`, sender: 'user', text },
      { id: `assistant-${Date.now() + 1}`, sender: 'assistant', text: reply.text, links: reply.links },
    ]);
    setInputValue('');
    setIsOpen(true);
  };

  return (
    <div className={isOpen ? 'ai-chat-widget open' : 'ai-chat-widget'}>
      {isOpen ? (
        <section className="ai-chat-panel" aria-label="Vedanskrix IT Training AI chat panel">
          <div className="ai-chat-header">
            <div className="ai-chat-brand">
              <img src="/logo.jpg" alt="Vedanskrix logo" />
              <div>
                <strong>IT Training AI Chat</strong>
                <span>Courses, placement, batches, and enquiries</span>
              </div>
            </div>

            <button type="button" className="ai-chat-close" aria-label="Close AI chat" onClick={() => setIsOpen(false)}>
              x
            </button>
          </div>

          <div className="ai-chat-messages">
            {messages.map((message) => (
              <div key={message.id} className={message.sender === 'assistant' ? 'ai-chat-bubble assistant' : 'ai-chat-bubble user'}>
                <p>{message.text}</p>
                {message.links?.length ? (
                  <div className="ai-chat-links">
                    {message.links.map((link) =>
                      link.to ? (
                        <Link key={`${message.id}-${link.label}`} to={link.to}>
                          {link.label}
                        </Link>
                      ) : (
                        <a key={`${message.id}-${link.label}`} href={link.href}>
                          {link.label}
                        </a>
                      )
                    )}
                  </div>
                ) : null}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div className="ai-chat-prompts">
            {quickPrompts.map((prompt) => (
              <button key={prompt} type="button" onClick={() => sendMessage(prompt)}>
                {prompt}
              </button>
            ))}
          </div>

          <form
            className="ai-chat-form"
            onSubmit={(event) => {
              event.preventDefault();
              sendMessage(inputValue);
            }}
          >
            <input
              type="text"
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              placeholder="Ask about courses or placement..."
              aria-label="Type your question"
            />
            <button type="submit">Send</button>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        className="ai-chat-launcher"
        aria-expanded={isOpen}
        aria-label="Open IT Training AI chat"
        onClick={() => setIsOpen((current) => !current)}
      >
        <img src="/logo.jpg" alt="Vedanskrix logo" />
        <span>AI Chat</span>
      </button>
    </div>
  );
}

export default AiChatWidget;
